import React, { useDeferredValue, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { proyectosPublicosApi } from '../../../api/api';
import type { ProyectoDTO } from '../../../api/Objetos';
import { ApiError } from '../../../api/http';
import { formatDate, formatMoney } from '../../shared/utils/format';
import { API_BASE_URL } from '../../../api/config';
import BarraCargando from "../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { slugify } from '../../shared/utils/slugify';

type ProyectoConSlug = ProyectoDTO & { slug: string };

export function PublicProjectsListPage() {
    const { t } = useTranslation();
    const [q, setQ] = useState('');
    const [estado, setEstado] = useState('');
    const qDeferred = useDeferredValue(q);
    const proyectosQ = useQuery({
        queryKey: ['public', 'proyectos', 'list', { q: qDeferred, estado }],
        queryFn: () => proyectosPublicosApi.list({ q: qDeferred, estado }),
        select: (data: unknown): ProyectoConSlug[] => {
            if (!Array.isArray(data)) {
                throw new Error('Respuesta de API inválida: no es un array.');
            }
            return data.map(proyecto => {
                if (typeof proyecto?.id !== 'number') {
                    throw new Error('Un elemento de la API no tiene el formato de proyecto esperado.');
                }
                const p = proyecto as ProyectoDTO;
                return { ...p, slug: slugify(p.informacionGeneral?.denominacion ?? '') };
            });
        },
    });
    const resumenQ = useQuery({
        queryKey: ['public', 'proyectos', 'summary'],
        queryFn: () => proyectosPublicosApi.list(),
    });

    const items = proyectosQ.data ?? [];

    const [estadosDisponibles, setEstadosDisponibles] = useState<string[]>([]);
    useMemo(() => {
        if (proyectosQ.data) {
            const values = new Set<string>(
                proyectosQ.data.map((item: ProyectoConSlug) => item.informacionGeneral?.estado).filter((s: string | null | undefined): s is string => !!s)
            );
            setEstadosDisponibles(Array.from(values).sort());
        }
    }, [proyectosQ.data]);


    const summaryItems: ProyectoDTO[] = resumenQ.data ?? [];
    const totalSubvencionado = summaryItems.reduce((acc, item) => acc + Number(item.informacionGeneral?.importeSubvencionado || 0), 0);
    const totalProyectos = summaryItems.reduce((acc, item) => acc + Number(item.informacionGeneral?.importeTotal || 0), 0);
    const categories = Array.from(summaryItems.reduce((map, item) => {
        const name = item.informacionGeneral?.ministerioConselleria?.trim();
        if (name) map.set(name, (map.get(name) ?? 0) + Number(item.informacionGeneral?.importeTotal || 0));
        return map;
    }, new Map<string, number>())).sort((a, b) => b[1] - a[1]);

    return (
        <div className="container stackLg">
            <section className="hero">
                <div className="heroCopy">
                    <span className="eyebrow">{t('publicProjectsList.eyebrow')}</span>
                    <h1>{t('publicProjectsList.title')}</h1>
                    <p>{t('publicProjectsList.description')}</p>
                </div>
                <div className="heroStats">
                    <div className="statCard">
                        <span className="statLabel">{t('publicProjectsList.visibleProjects')}</span>
                        <strong>
                            {resumenQ.isLoading ? <BarraCargando/> : summaryItems.length}
                        </strong>
                    </div>
                    <div className="statCard">
                        <span className="statLabel">Importe total de los proyectos</span>
                        <strong>
                            {resumenQ.isLoading ? <BarraCargando/> : formatMoney(totalProyectos)}
                        </strong>
                    </div>
                    <div className="statCard statCardGrant">
                        <span className="statLabel">{t('publicProjectsList.accumulatedGrant')}</span>
                        <strong>
                            {resumenQ.isLoading ? <BarraCargando/> : formatMoney(totalSubvencionado)}
                        </strong>
                    </div>
                </div>
            </section>

            {categories.length > 1 ? (
                <section className="fundingChart" aria-labelledby="funding-chart-title">
                    <div className="sectionHeading"><h2 id="funding-chart-title">INVERSIÓN POR ORGANISMO</h2></div>
                    <div className="fundingChartRows">
                        {categories.map(([name, amount]) => (
                            <div className="fundingChartRow" key={name}>
                                <div className="fundingChartLabel"><span>{name}</span><strong>{formatMoney(amount)}</strong></div>
                                <div className="fundingChartTrack"><span style={{width: `${totalProyectos ? Math.max(2, amount / totalProyectos * 100) : 0}%`}}/></div>
                            </div>
                        ))}
                    </div>
                </section>
            ) : null}

            <section className="filterBar">
                <label className="label">
                    {t('publicProjectsList.search')}
                    <input
                        className="input"
                        placeholder={t('publicProjectsList.searchPlaceholder')}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                </label>
                <label className="label">
                    {t('publicProjectsList.status')}
                    <select className="input" value={estado} onChange={(e) => setEstado(e.target.value)}>
                        <option value="">{t('publicProjectsList.all')}</option>
                        {estadosDisponibles.map((item) => (
                            <option key={item} value={item}>
                                {item}
                            </option>
                        ))}
                    </select>
                </label>
            </section>

            {proyectosQ.isLoading ? <p>{t('publicProjectsList.loading')}</p> : null}
            {proyectosQ.isError ? (
                <div className="alert alertError">
                    <strong>{t('publicProjectsList.errorTitle')}</strong>
                    <div className="muted">{renderProjectsError(proyectosQ.error, t)}</div>
                </div>
            ) : null}

            <div className="cardGrid">
                {/* CORRECCIÓN 3: Añadir tipo al parámetro 'proyecto' del map */}
                {items.map((proyecto: ProyectoConSlug) => (
                    <Link key={proyecto.id} to={`/proyectos/${proyecto.slug}`} className="projectCard">
                        <div className="projectCardTop">
                            <span className="pill">{proyecto.informacionGeneral?.estado || t('publicProjectsList.noStatus')}</span>
                        </div>
                        <h2>{proyecto.informacionGeneral?.denominacion || `${t('publicProjectsList.project')} ${proyecto.id}`}</h2>
                        <dl className="facts">
                            <Fact label={t('publicProjectsList.ministry')} value={proyecto.informacionGeneral?.ministerioConselleria}/>
                            <Fact label={t('publicProjectsList.request')} value={formatDate(proyecto.informacionGeneral?.fechaSolicitud)}/>
                            <Fact label={t('publicProjectsList.grant')} value={formatMoney(proyecto.informacionGeneral?.importeSubvencionado)}/>
                        </dl>
                    </Link>
                ))}
            </div>

            {!proyectosQ.isLoading && items.length === 0 ? (
                <div className="emptyState">
                    <h2>{t('publicProjectsList.noResultsTitle')}</h2>
                    <p className="muted">{t('publicProjectsList.noResultsDescription')}</p>
                </div>
            ) : null}
        </div>
    );
}

function renderProjectsError(error: unknown, t: TFunction<"translation", undefined>) {
    if (error instanceof ApiError) {
        if (error.status === 0) {
            return t('publicProjectsList.apiErrorConnect', { url: API_BASE_URL });
        }
        return `${error.status}: ${error.message}`;
    }
    if (error instanceof Error) return error.message;
    return t('publicProjectsList.apiErrorUnknown');
}

function Fact({label, value}: { label: string; value: string | number | null | undefined }) {
    return (
        <div className="fact">
            <dt>{label}</dt>
            <dd>{value || '-'}</dd>
        </div>
    );
}
